import { FormSectionHandler } from '../entity.handler';
import { UserResponse, ApiKey } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { EntityUtils, TextField } from '../utils';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';

interface EditorValues {
    keys: ListFieldValue<ApiKey>[];
}

export class UserApiKeysSectionHandler implements FormSectionHandler<UserResponse, EditorValues> {

    type = 'form';
    icon = 'key';
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    userId: string;
    buttons = ['ok']; 
    modified = false;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.USER.API_KEYS.${key}`, values);
    }

    async applyUpdate(entity: UserResponse, _payload: any): Promise<UserResponse> {
        // we don't support saving at the end in this handler, just get the latest version of the entity
        return this.modified ? await this.admin.getCached(`getUser/${entity.id}`, api => api.accounts.getUser(entity.id), 0) : entity;
    }

    async mapEntityToEditor(entity: UserResponse): Promise<EditorValues> {
        this.userId = entity.id;
        return {
            keys: Object.keys(entity.apiKeys).map(id => this.itemToListItem(id, entity.apiKeys[id])),
        };
    }

    async mapEditorToEntity(_entity: any, _values: EditorValues): Promise<any> {
        return {}; // we don't support saving
    }

    async getFields(entity: UserResponse): Promise<FormBuilderField[]> {
        const fields: FormBuilderField[] = [
            {
                id: 'keys', title: this.translate('FIELDS.KEYS'), type: 'list', required: false,
                actions: [
                    { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showCreateModal.bind(this), },
                    { id: 'delete', click: this.deleteItem.bind(this) },
                ],
                getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
            },
        ];
        return EntityUtils.prepareBulk(!entity, [], fields);
    }


    async deleteItem(_action: any, _field: FormBuilderField, _values: any, item?: ListFieldValue<ApiKey>): Promise<boolean> {
        try {
            const result = await this.admin.modal.confirm(this.translate('MODALS.DELETE.DESC'));
            if (result) {
                this.modified = true;
                await this.admin.applyUpdate(api => api.accounts.deleteApiKey(this.userId, item.id));
            }
            return result;
        } catch (err) {
            await this.admin.modal.error(err.message);
            return false;
        }
    }

    itemToListItem(id: string, item: ApiKey): ListFieldValue<ApiKey> {
        return {
            id: id,
            name: item.name,
            description: id,
            icon: 'password',
            data: item,
        };
    }

    updateOrAddItem(items: ListFieldValue<ApiKey>[], existingItem: ListFieldValue<ApiKey>, id: string, item: ApiKey) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.itemToListItem(id, item);
        } else {
            items.push(this.itemToListItem(id, item));
        }
    }

    async showCreateModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<ApiKey>): Promise<ApiKey> {

        try {

            const details = await this.admin.showFormModal<ApiKey>({
                groups: [{
                    name: this.translate('MODALS.NEW.TITLE'),
                    description: this.translate('MODALS.NEW.DESC'),
                    fields: [
                        TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                    ],
                }],
            }, (item && item.data) ||
            { // pass some defaults for new items
                name: '',
            }
            );
            if (details) {

                this.modified = true;
                const result = await this.admin.applyUpdate(api => api.accounts.createApiKey(this.userId, {
                    name: details.name,
                    policies: [], // We don't support this from the UI yet
                }));

                await this.admin.modal.alert(`${this.translate('MODALS.DETAILS.DESC')}<br/><br/><pre>${result.key}<pre>`, this.translate('MODALS.DETAILS.TITLE'));

                this.updateOrAddItem(values.keys, item, result.key.split('.')[0], {
                    name: result.name,
                    policies: result.policies,
                });
            }
        } catch (err) {
            await this.admin.modal.error(err.message);
            return null;
        }

    }

}

